import type { ChartConfiguration } from "chart.js";
import Chart from "chart.js/auto";
import { useLayoutEffect, useMemo, useRef } from "react";
import { angularize } from "react-in-angularjs";
import { drawAverageChartjsPlugin } from "./_drawHistogramAverage.plugin";

type ChartColorType = "backgroundColor" | "borderColor" | "color";

interface ChartJsProps {
  data: ChartConfiguration & {
    colors: Record<ChartColorType, string>;
  };
  average: {
    standardDeviation: number;
    average: number;
  };
  width: string;
  maxWidth: string;
  height: string;
  maxHeight: string;
}

(() => {
  // default chart defaults

  const getColorByCSSVariableName = (variableName: string) => {
    const propertyValue = getComputedStyle(document.documentElement).getPropertyValue(
      variableName
    );
    if (propertyValue && propertyValue !== "") {
      return propertyValue;
    }

    return;
  };

  const getChartColorPrimary = (fallback: any) =>
    getColorByCSSVariableName("--chartColorPrimary") || fallback;

  const getChartColorPrimaryTransparent = (fallback: any) =>
    getColorByCSSVariableName("--chartColorPrimaryTransparent") || fallback;

  // set default config
  Chart.defaults.backgroundColor = getChartColorPrimary(Chart.defaults.backgroundColor);

  // set line config
  const { backgroundColor, borderColor } = Chart.defaults.elements.line;
  Chart.defaults.elements.line = {
    ...Chart.defaults.elements.line,
    backgroundColor: getChartColorPrimaryTransparent(backgroundColor),
    borderColor: getChartColorPrimary(borderColor),
    borderWidth: 2,
    fill: "origin",
    tension: 0.4
  };
})();

const ChartJs = ({
  data: chartData,
  average: chartAverage,
  width,
  height,
  maxWidth,
  maxHeight
}: ChartJsProps) => {
  const chartRef = useRef<HTMLCanvasElement>(null);

  // Several AngularJS templates set only one of the two (participation.progress
  // sizes its width in CSS), so this is undefined rather than NaN when either
  // side is missing, and Chart.js and the CSS fall back to their own defaults.
  const aspectRatio = useMemo(() => {
    const ratio = parseInt(width) / parseInt(height);
    return Number.isFinite(ratio) && ratio > 0 ? ratio : undefined;
  }, [width, height]);

  useLayoutEffect(
    function renderChart() {
      const { current: chartElement } = chartRef;
      if (!chartElement) {
        return;
      }

      const configuration = {
        ...chartData,
        options: {
          ...chartData.options,
          plugins: {
            ...chartData.options?.plugins,
            legend: {
              display: false
            },
            ...(chartAverage && { draw_average: chartAverage })
          },
          responsive: true,
          maintainAspectRatio: true,
          aspectRatio
        },
        plugins: chartAverage ? [drawAverageChartjsPlugin] : chartData.plugins
      };

      // Chart.js refuses a canvas that still holds a chart, so tear the old one down first.
      const chart = new Chart(chartElement, configuration);

      return () => {
        chart.destroy();
      };
    },
    [aspectRatio, chartAverage, chartData]
  );

  return (
    <div
      style={{
        aspectRatio,
        width: "100%",
        maxHeight,
        maxWidth,
        position: "relative"
      }}
    >
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

export default ChartJs;

angularize(ChartJs, "chartJs", angular.module("diceApp"), {
  data: "<",
  height: "<?",
  average: "<?",
  maxHeight: "<?",
  maxWidth: "<?",
  width: "<?"
});
